import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import config from '../config';

interface SubmissionReviewProps {
  attemptId?: number | string;
  embedded?: boolean;
  onClose?: () => void;
  onOpenFullPage?: (attemptId: number) => void;
}

interface Answer {
  questionId: number;
  questionText: string;
  questionLatex?: string | null;
  questionImage?: string;
  questionType?: string;
  studentSelectedOption: string;
  studentAnswerText?: string;
  correctOption: string;
  options: string[];
  marks: number;
  isCorrect: boolean;
  teacherComment?: string;
}

interface AttemptDetail {
  id: number;
  assignmentId: number;
  studentName: string;
  studentEmail: string;
  attemptNumber: number;
  status: string;
  startedAt: string;
  submittedAt?: string;
  updatedAt?: string;
  teacherComment?: string;
  answers: Answer[];
}

interface ExamAttemptQuestionResult {
  questionId: number;
  questionOrder: number;
  questionText: string;
  questionLatex?: string | null;
  selectedOption?: string | null;
  correctOption?: string | null;
  isCorrect: boolean;
  marksAwarded: number;
  maxMarks: number;
  explanation?: string | null;
  teacherComment?: string | null;
}

interface ExamAttemptResult {
  id: number;
  examId: number;
  examName: string;
  attemptNumber: number;
  status: string;
  timeSpentSeconds: number;
  totalQuestions: number;
  attemptedQuestions: number;
  correctCount: number;
  incorrectCount: number;
  rawScore: number;
  totalMarks: number;
  percentage: number;
  isPassed: boolean;
  startedAt: string;
  completedAt?: string | null;
  payloadVersion: number;
  attemptSnapshotJson?: string | null;
  detailedResults: ExamAttemptQuestionResult[];
}

const prepareForMathJax = (value?: string | null): string => {
  const text = (value || '').trim();
  if (!text) return '';

  const hasDelimiters = /\$|\\\(|\\\[/.test(text);
  const looksLikeLatex = /\\[a-zA-Z]+|\^|_/.test(text);

  if (!hasDelimiters && looksLikeLatex) {
    return `\\(${text}\\)`;
  }

  return text;
};

const toHtmlMarkup = (value?: string | null): string => {
  const prepared = prepareForMathJax(value);
  if (!prepared) return '';

  const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(prepared);
  if (hasHtmlTags) {
    return prepared;
  }

  const escaped = prepared
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  return escaped.replace(/\r?\n/g, '<br/>');
};

const mapExamAttemptToAttemptDetail = (data: ExamAttemptResult): AttemptDetail => ({
  id: data.id,
  assignmentId: 0,
  studentName: data.examName,
  studentEmail: '',
  attemptNumber: data.attemptNumber,
  status: data.status,
  startedAt: data.startedAt,
  submittedAt: data.completedAt || undefined,
  updatedAt: data.completedAt || undefined,
  teacherComment: '',
  answers: data.detailedResults.map((question) => ({
    questionId: question.questionId,
    questionText: question.questionText,
    questionLatex: question.questionLatex,
    questionImage: undefined,
    questionType: undefined,
    studentSelectedOption: question.selectedOption || '',
    studentAnswerText: question.selectedOption || 'Not answered',
    correctOption: question.correctOption || 'N/A',
    options: [],
    marks: question.maxMarks,
    isCorrect: question.isCorrect,
    teacherComment: question.teacherComment || '',
  })),
});

const SubmissionReview: React.FC<SubmissionReviewProps> = ({
  attemptId: providedAttemptId,
  embedded = false,
  onClose,
  onOpenFullPage,
}) => {
  const { attemptId: routeAttemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [attempt, setAttempt] = useState<AttemptDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [questionFeedback, setQuestionFeedback] = useState<Record<number, string>>({});
  const [reviewMode, setReviewMode] = useState<'assignment' | 'exam'>('assignment');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const resolvedAttemptId = String(providedAttemptId ?? routeAttemptId ?? '').trim();
  const parsedAttemptId = Number(resolvedAttemptId);
  const canOpenFullPage = embedded && Number.isFinite(parsedAttemptId) && parsedAttemptId > 0;

  useEffect(() => {
    if (!resolvedAttemptId) {
      setAttempt(null);
      setNotice({ type: 'error', message: 'Attempt id is missing.' });
      setLoading(false);
      return;
    }

    const fetchAttempt = async () => {
      try {
        setLoading(true);
        const token = await getAccessTokenSilently();
        const assignmentResp = await fetch(
          `${config.apiBaseUrl}/api/assignments/attempts/${resolvedAttemptId}/review-details`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (assignmentResp.ok) {
          const data = await assignmentResp.json();
          setAttempt(data);
          setReviewMode('assignment');
          setComment(data.teacherComment || '');

          const feedback: Record<number, string> = {};
          data.answers?.forEach((ans: Answer) => {
            if (ans.teacherComment) {
              feedback[ans.questionId] = ans.teacherComment;
            }
          });
          setQuestionFeedback(feedback);
          return;
        }

        const examResp = await fetch(`${config.apiBaseUrl}/api/examattempts/${resolvedAttemptId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!examResp.ok) {
          throw new Error('Failed to load attempt');
        }

        const data: ExamAttemptResult = await examResp.json();
        const mapped = mapExamAttemptToAttemptDetail(data);
        setAttempt(mapped);
        setReviewMode('exam');
        setComment('');

        const feedback: Record<number, string> = {};
        mapped.answers?.forEach((ans: Answer) => {
          if (ans.teacherComment) {
            feedback[ans.questionId] = ans.teacherComment;
          }
        });
        setQuestionFeedback(feedback);
      } catch (err) {
        console.error(err);
        setNotice({ type: 'error', message: 'Failed to load submission.' });
      } finally {
        setLoading(false);
      }
    };
    fetchAttempt();
  }, [resolvedAttemptId, getAccessTokenSilently]);

  const handleSaveFeedback = async () => {
    if (!attempt) return;
    try {
      setSaving(true);
      setNotice(null);
      const token = await getAccessTokenSilently();

      if (reviewMode === 'assignment') {
        const questionFeedbackArray = attempt.answers
          .filter(ans => questionFeedback[ans.questionId])
          .map(ans => ({
            questionId: ans.questionId,
            comment: questionFeedback[ans.questionId]
          }));

        const resp = await fetch(
          `${config.apiBaseUrl}/api/assignments/attempts/${resolvedAttemptId}/save-feedback`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              teacherComment: comment,
              questionFeedback: questionFeedbackArray
            }),
          }
        );
        if (!resp.ok) throw new Error('Failed to save feedback');
        setNotice({ type: 'success', message: 'Feedback saved successfully.' });
        return;
      }

      const payload = {
        teacherComment: comment,
        answers: attempt.answers.map((ans) => ({
          questionId: ans.questionId,
          isCorrect: ans.isCorrect,
          marksAwarded: ans.marks,
          teacherComment: questionFeedback[ans.questionId] || undefined,
        })),
      };

      const resp = await fetch(`${config.apiBaseUrl}/api/examattempts/${resolvedAttemptId}/review`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) throw new Error('Failed to save feedback');
      setNotice({ type: 'success', message: 'Feedback saved successfully.' });
    } catch (err) {
      console.error(err);
      setNotice({ type: 'error', message: 'Failed to save feedback. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!attempt) return <div className="p-6">Attempt not found</div>;

  return (
    <MathJaxContext>
      <div className={embedded ? 'p-4 bg-white' : 'p-6 bg-white'}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">{attempt.studentName}</h1>
          <p className="text-gray-600">{attempt.studentEmail}</p>
          <p className="text-sm text-gray-500 mt-2">
            {reviewMode === 'exam' ? 'Exam submission' : 'Assignment submission'} • Attempt #{attempt.attemptNumber} • Status: <span className="font-semibold">{attempt.status}</span>
          </p>
          <p className="text-sm text-gray-500">
            Started: {new Date(attempt.startedAt).toLocaleString()}
            {attempt.submittedAt && ` • Submitted: ${new Date(attempt.submittedAt).toLocaleString()}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canOpenFullPage && onOpenFullPage && (
            <button
              onClick={() => onOpenFullPage(parsedAttemptId)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Open Full Page
            </button>
          )}
          <button
            onClick={() => {
              if (embedded && onClose) {
                onClose();
                return;
              }
              navigate(-1);
            }}
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            {embedded ? 'Close' : '← Back'}
          </button>
        </div>
      </div>

      <div className="space-y-8">
        {attempt.answers.map((answer, idx) => (
          <div key={answer.questionId} className="border rounded-lg p-4 bg-gray-50">
            <div className="flex items-start gap-4">
              <span className="font-semibold text-lg min-w-fit">Q{idx + 1}</span>
              <div className="flex-1">
                {/* Question */}
                <div className="mb-4">
                  <MathJax dynamic>
                    <div dangerouslySetInnerHTML={{ __html: toHtmlMarkup(answer.questionLatex || answer.questionText) }} />
                  </MathJax>
                  {answer.questionImage && (
                    <img src={answer.questionImage} alt="Question" className="mt-2 max-h-48" />
                  )}
                </div>

                {/* Answer Comparison */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Student's Answer */}
                  <div className="bg-white p-3 rounded border">
                    <h4 className="font-semibold text-sm text-gray-700 mb-2">Student's Answer</h4>
                    {answer.options.length > 0 ? (
                      <div className="space-y-1">
                        {answer.options.map((opt, i) => {
                          const optLetter = String.fromCharCode(65 + i); // A, B, C, D
                          const isSelected = answer.studentSelectedOption === optLetter;
                          return (
                            <label key={i} className="flex items-center gap-2">
                              <input
                                type="radio"
                                checked={isSelected}
                                readOnly
                                className={isSelected ? 'accent-blue-500' : ''}
                              />
                              <span className={isSelected ? 'font-semibold' : ''}>
                                {optLetter}. 
                                <MathJax inline dynamic>
                                  <span dangerouslySetInnerHTML={{ __html: toHtmlMarkup(opt) }} />
                                </MathJax>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-gray-600">
                        <MathJax dynamic>
                          <div dangerouslySetInnerHTML={{ __html: toHtmlMarkup(answer.studentAnswerText) }} />
                        </MathJax>
                      </div>
                    )}
                  </div>

                  {/* Correct Answer */}
                  <div className="bg-green-50 p-3 rounded border border-green-300">
                    <h4 className="font-semibold text-sm text-gray-700 mb-2">Correct Answer</h4>
                    {answer.options.length > 0 ? (
                      <div className="space-y-1">
                        {answer.options.map((opt, i) => {
                          const optLetter = String.fromCharCode(65 + i);
                          const isCorrect = answer.correctOption === optLetter;
                          return (
                            <div
                              key={i}
                              className={`flex items-center gap-2 ${isCorrect ? 'font-semibold text-green-700' : 'text-gray-600'}`}
                            >
                              <span>{isCorrect ? '✓' : '•'}</span>
                              <span>
                                {optLetter}. 
                                <MathJax inline dynamic>
                                  <span dangerouslySetInnerHTML={{ __html: toHtmlMarkup(opt) }} />
                                </MathJax>
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-gray-600">
                        <MathJax dynamic>
                          <div dangerouslySetInnerHTML={{ __html: toHtmlMarkup(answer.correctOption) }} />
                        </MathJax>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status Badge */}
                <div className="mt-3 flex items-center gap-2">
                  <span className={`px-3 py-1 rounded text-sm font-semibold ${
                    answer.isCorrect
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {answer.isCorrect ? '✓ Correct' : '✗ Incorrect'}
                  </span>
                  <span className="text-sm text-gray-600">Marks: {answer.marks}</span>
                </div>

                {/* Question Feedback */}
                <div className="mt-4 pt-4 border-t">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Feedback for this question (optional)
                  </label>
                  <textarea
                    value={questionFeedback[answer.questionId] || ''}
                    onChange={(e) => setQuestionFeedback({
                      ...questionFeedback,
                      [answer.questionId]: e.target.value
                    })}
                    placeholder="Add specific feedback for this question..."
                    className="w-full p-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    rows={2}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Feedback Section */}
      <div className="mt-8 border-t pt-6">
        <h3 className="text-lg font-semibold mb-3">Teacher Feedback</h3>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add comments for the student..."
          className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={4}
        />
        {notice && (
          <div className={`mt-3 rounded-lg border px-4 py-3 text-sm ${notice.type === 'success'
            ? 'border-green-300 bg-green-50 text-green-800'
            : 'border-red-300 bg-red-50 text-red-800'}`}>
            {notice.message}
          </div>
        )}
        <button
          onClick={handleSaveFeedback}
          disabled={saving}
          className="mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
        >
          {saving ? 'Saving...' : 'Save Feedback'}
        </button>
      </div>
    </div>
    </MathJaxContext>
  );
};

export default SubmissionReview;
